import { useEffect, useState } from 'react';

/**
 * Live clock + short date in the user's local timezone. Lives in the Topbar.
 *
 * Renders something like:  Sat 17 May - 16:30 AEST
 *
 * Updates every 30 seconds. The timezone abbreviation is derived from the
 * Intl API (the browser figures out the locale's short tz name itself).
 */
function format(now: Date): { date: string; time: string; zone: string } {
  // Intl handles AEST/AEDT/etc. automatically based on the user's locale +
  // current date (daylight savings). `timeZoneName: 'short'` is the bit.
  const dateStr = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(now);
  const timeParts = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(now);
  const time = `${timeParts.find((p) => p.type === 'hour')?.value ?? ''}:${timeParts.find((p) => p.type === 'minute')?.value ?? ''}`;
  const zone = timeParts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  return { date: dateStr, time, zone };
}

export function LocalClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Tick on the next round 30-second boundary so the clock minute ticks
    // line up with real-world minute boundaries (less wall-clock skew when
    // many users see the same value).
    const msUntilNext30s = 30_000 - (Date.now() % 30_000);
    const id = setTimeout(function tick() {
      setNow(new Date());
      // After the first aligned tick, run on a regular 30-second interval.
      const interval = window.setInterval(() => setNow(new Date()), 30_000);
      // Cleanup the interval when the component unmounts. We re-assign id
      // to the interval handle so the outer useEffect cleanup clears it.
      (tick as unknown as { _interval?: number })._interval = interval;
    }, msUntilNext30s);
    return () => {
      clearTimeout(id);
      const interval = (id as unknown as { _interval?: number })._interval;
      if (interval !== undefined) clearInterval(interval);
    };
  }, []);

  const { date, time, zone } = format(now);

  return (
    <div
      className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg
                 text-xs text-slate-600 dark:text-slate-400 select-none"
      title={`${date} ${time} ${zone}`}
      aria-label={`Local time: ${date}, ${time} ${zone}`}
    >
      <ClockIcon />
      <span className="font-medium text-slate-700 dark:text-slate-300">{time}</span>
      <span className="text-slate-400 dark:text-slate-500">{zone}</span>
      <span className="text-slate-300 dark:text-slate-600">·</span>
      <span>{date}</span>
    </div>
  );
}

function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
